import React from "react";
import { AlertTriangle, Info, X, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Character confusion sets
const CONFUSION_SETS = [
  { chars: ["O", "0", "Q"], label: "Round characters" },
  { chars: ["I", "1", "L"], label: "Vertical lines" },
  { chars: ["B", "8"], label: "Similar shapes" },
  { chars: ["S", "5"], label: "S-curves" },
  { chars: ["Z", "2"], label: "Angular shapes" },
  { chars: ["G", "6"], label: "Similar curves" },
];

interface CharacterSwap {
  position: number;
  entryChar: string;
  exitChar: string;
  confusionGroup: string;
}

function findConfusionGroup(char1: string, char2: string): string | null {
  for (const set of CONFUSION_SETS) {
    if (
      set.chars.includes(char1.toUpperCase()) &&
      set.chars.includes(char2.toUpperCase())
    ) {
      return set.label;
    }
  }
  return null;
}

function analyzeSwaps(entryPlate: string, exitPlate: string): CharacterSwap[] {
  const swaps: CharacterSwap[] = [];
  const maxLen = Math.max(entryPlate.length, exitPlate.length);

  for (let i = 0; i < maxLen; i++) {
    const entryChar = entryPlate[i] || "";
    const exitChar = exitPlate[i] || "";

    if (
      entryChar &&
      exitChar &&
      entryChar.toUpperCase() !== exitChar.toUpperCase()
    ) {
      const group = findConfusionGroup(entryChar, exitChar);
      swaps.push({
        position: i + 1,
        entryChar: entryChar.toUpperCase(),
        exitChar: exitChar.toUpperCase(),
        confusionGroup: group || "Other difference",
      });
    }
  }

  return swaps;
}

interface FuzzyMatchingInfoModalProps {
  entryPlate?: string;
  exitPlate?: string;
}

export function FuzzyMatchingInfoModal({
  entryPlate,
  exitPlate,
}: FuzzyMatchingInfoModalProps) {
  const swaps =
    entryPlate && exitPlate ? analyzeSwaps(entryPlate, exitPlate) : [];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Info className="h-4 w-4" />
          Fuzzy Match Rules
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Fuzzy Matching Rules
          </DialogTitle>
          <DialogDescription>
            Understanding how the system handles OCR (camera reading) errors and
            character confusion
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Show actual swaps if we have plate data */}
          {swaps.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border-2 border-amber-200 dark:border-amber-800">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Character Swaps in This Match
              </h4>

              <div className="flex items-center justify-center gap-3 mb-4 p-3 bg-white dark:bg-slate-900 rounded">
                <span className="font-mono text-lg font-bold">
                  {entryPlate}
                </span>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
                <span className="font-mono text-lg font-bold">{exitPlate}</span>
              </div>

              <div className="space-y-2">
                {swaps.map((swap, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2 bg-white dark:bg-slate-900 rounded"
                  >
                    <Badge variant="outline" className="font-mono">
                      Position {swap.position}
                    </Badge>
                    <span className="font-mono text-lg font-bold text-red-600">
                      {swap.entryChar}
                    </span>
                    <ArrowRight className="h-4 w-4" />
                    <span className="font-mono text-lg font-bold text-green-600">
                      {swap.exitChar}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ({swap.confusionGroup})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Overview */}
          <div>
            <h4 className="font-semibold mb-2">What is Fuzzy Matching?</h4>
            <p className="text-sm text-muted-foreground">
              Camera systems sometimes misread license plates due to angles,
              lighting, or dirt. Fuzzy matching helps pair entries and exits
              even when the plate isn't read identically both times.
            </p>
          </div>

          {/* Complete Character Swap Rules - Compact Grid */}
          <div>
            <h4 className="font-semibold mb-3">
              Complete Character Swap Rules
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              The system allows these specific character swaps when matching
              plates:
            </p>

            <div className="grid grid-cols-3 gap-4">
              {/* Group 1: O, 0, Q */}
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                <h5 className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-300">
                  Round Characters
                </h5>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="font-mono text-2xl font-bold">O</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">0</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">Q</span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Letter O, Zero, Letter Q
                </p>
              </div>

              {/* Group 2: I, 1, L */}
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                <h5 className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-300">
                  Vertical Lines
                </h5>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="font-mono text-2xl font-bold">I</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">1</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">L</span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Letter I, One, Letter L
                </p>
              </div>

              {/* Group 3: B, 8 */}
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                <h5 className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-300">
                  Loops
                </h5>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="font-mono text-2xl font-bold">B</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">8</span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Letter B, Eight
                </p>
              </div>

              {/* Group 4: S, 5 */}
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                <h5 className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-300">
                  S-Curves
                </h5>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="font-mono text-2xl font-bold">S</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">5</span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Letter S, Five
                </p>
              </div>

              {/* Group 5: Z, 2 */}
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                <h5 className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-300">
                  Angular Shapes
                </h5>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="font-mono text-2xl font-bold">Z</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">2</span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Letter Z, Two
                </p>
              </div>

              {/* Group 6: G, 6 */}
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                <h5 className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-300">
                  Curves
                </h5>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="font-mono text-2xl font-bold">G</span>
                  <span className="text-slate-400">↔</span>
                  <span className="font-mono text-2xl font-bold">6</span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Letter G, Six
                </p>
              </div>
            </div>
          </div>

          {/* Matching Constraints - Compact */}
          <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Matching Constraints</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-primary">•</span>
                <span>
                  <strong>Max 2 differences</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">•</span>
                <span>
                  <strong>Same length (±1 char)</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">•</span>
                <span>
                  <strong>Position-weighted</strong> (first/last more important)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">•</span>
                <span>
                  <strong>Confusion groups only</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Examples - More Compact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <h4 className="font-semibold mb-2 text-green-600">
                ✓ Valid Matches
              </h4>
              <div className="space-y-1.5 text-sm">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                  <div className="font-mono">ABC123 → ABC1Z3</div>
                  <div className="text-xs text-muted-foreground">2 ↔ Z</div>
                </div>
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                  <div className="font-mono">O0OO00 → QQOOOO</div>
                  <div className="text-xs text-muted-foreground">
                    O ↔ Q, 0 ↔ O
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2 text-red-600">✗ Not Matches</h4>
              <div className="space-y-1.5 text-sm">
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                  <div className="font-mono">ABC123 → XYZ789</div>
                  <div className="text-xs text-muted-foreground">
                    Too many diffs
                  </div>
                </div>
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                  <div className="font-mono">ABC → ABCD</div>
                  <div className="text-xs text-muted-foreground">
                    Length mismatch
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
